import { reactive, ref, computed, watch, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";
import { api } from "@/services/api";
import locations from "@/assets/gulf_locations.json";

// ── Leaflet loader ────────────────────────────
let leafletLoadPromise = null;

function loadLeaflet() {
  if (leafletLoadPromise) return leafletLoadPromise;

  leafletLoadPromise = new Promise((resolve, reject) => {
    if (!document.getElementById("leaflet-css")) {
      const link  = document.createElement("link");
      link.id     = "leaflet-css";
      link.rel    = "stylesheet";
      link.href   = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css";
      document.head.appendChild(link);
    }

    if (window.L) { resolve(window.L); return; }

    const script   = document.createElement("script");
    script.src     = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
    script.onload  = () => resolve(window.L);
    script.onerror = () => reject(new Error("Failed to load Leaflet"));
    document.head.appendChild(script);
  });

  return leafletLoadPromise;
}

export function useAddBranchForm() {
  const apiUrl = process.env.VUE_APP_API_URL;
  const router = useRouter();

  const loading        = ref(false);
  const locating       = ref(false);
  const error          = ref(null);
  const locationStatus = ref("");

  // ── Map state ─────────────────────────────────
  let leafletMap    = null;
  let leafletMarker = null;
  const mapReady    = ref(false);

  // ── Image state ───────────────────────────────
  const imageName             = ref("");
  const imagePreview          = ref("");
  let   imagePreviewObjectUrl = null;

  // ── Form ──────────────────────────────────────
  // Same field names as before. Categories and business_hours removed —
  // payload no longer sends those keys.
  const form = reactive({
    company_name:    "",
    company_name_ar: "",
    company_name_en: "",
    preferred_lang:  "ar",

    district:     "",
    city:         "",
    country:      "",

    latitude:     "",
    longitude:    "",
    location_URL: "",

    main_image:   null,
    note:         "",
  });

  // ── Display name sync ──────────────────────────
  // Auto-picks the preferred language based on what the user filled in.
  // No more radio toggle — if both are filled, AR wins (it was the default).
  function syncPreferredName() {
    const ar = (form.company_name_ar || "").trim();
    const en = (form.company_name_en || "").trim();
    if (ar && !en) form.preferred_lang = "ar";
    else if (en && !ar) form.preferred_lang = "en";
    else if (ar && en) form.preferred_lang = "ar"; // both filled → AR
    form.company_name = form.preferred_lang === "ar" ? (ar || en) : (en || ar);
  }

  watch(() => [form.company_name_ar, form.company_name_en], () => syncPreferredName());

  const hasName = computed(() =>
    !!(form.company_name_ar?.trim() || form.company_name_en?.trim())
  );

  // ── Locations ─────────────────────────────────
  const cityQuery        = ref("");
  const cityDropdownOpen = ref(false);
  const countries        = locations.countries;

  const saCities = computed(() => {
    const sa = countries.find(c => c.code === "SA");
    return sa?.cities || [];
  });

  const citiesForCountry = computed(() => {
    const c = countries.find(x => x.name_ar === form.country);
    return c?.cities || [];
  });

  // Filtered Saudi cities — driven by what the user typed.
  // Capped at 50 for render perf; users can refine with more characters.
  const filteredCities = computed(() => {
    if (form.country !== "السعودية") return [];
    const q = (cityQuery.value || "").trim().toLowerCase();
    if (!q) return saCities.value.slice(0, 50);
    return saCities.value
      .filter(c =>
        c.name_ar?.includes(q) ||
        (c.name_en && c.name_en.toLowerCase().includes(q))
      )
      .slice(0, 50);
  });

  // City is valid only if it matches a real entry in the list.
  // For Saudi: typed/picked value must be in saCities.
  // For other countries: <select> only allows valid values, so non-empty = valid.
  const cityIsValid = computed(() => {
    if (!form.city) return false;
    if (form.country !== "السعودية") return true;
    return saCities.value.some(c => c.name_ar === form.city);
  });

  function pickCity(c) {
    cityQuery.value        = c.name_ar;
    form.city              = c.name_ar;
    cityDropdownOpen.value = false;
  }

  function clearCity() {
    cityQuery.value        = "";
    form.city              = "";
    cityDropdownOpen.value = true;
  }

  function onCityFocus() {
    if (form.country === "السعودية") cityDropdownOpen.value = true;
  }

  // Delay close so a tap on a dropdown option lands before the dropdown unmounts.
  // 300ms covers iOS Safari's touch-to-click latency comfortably.
  function onCityBlur() {
    setTimeout(() => { cityDropdownOpen.value = false; }, 300);
  }

  watch(cityQuery, (val) => { if (form.country === "السعودية") form.city = val; });
  watch(() => form.country, () => {
    form.city              = "";
    cityQuery.value        = "";
    cityDropdownOpen.value = false;
  });

  // ── Coords ────────────────────────────────────
  const hasCoords = computed(() => !!(form.latitude && form.longitude));

  const coordsLabel = computed(() => {
    if (!hasCoords.value) return "";
    return `${Number(form.latitude).toFixed(5)}, ${Number(form.longitude).toFixed(5)}`;
  });

  // ── Map pin icon ──────────────────────────────
  function makePinIcon(L) {
    return L.divIcon({
      className: "",
      html: `<div style="
        width:18px;height:18px;border-radius:50%;
        background:linear-gradient(135deg,#3B9EFF,#00CFC2);
        border:3px solid white;
        box-shadow:0 2px 12px rgba(59,158,255,0.6);
      "></div>`,
      iconSize:   [18, 18],
      iconAnchor: [9, 9],
    });
  }

  // ── Place / move marker ───────────────────────
  function placeMarker(L, lat, lng) {
    form.latitude  = String(lat.toFixed(7));
    form.longitude = String(lng.toFixed(7));
    locationStatus.value = "";

    if (leafletMarker) {
      leafletMarker.setLatLng([lat, lng]);
    } else {
      leafletMarker = L.marker([lat, lng], {
        icon:      makePinIcon(L),
        draggable: true,
      }).addTo(leafletMap);

      leafletMarker.on("dragend", (e) => {
        const pos = e.target.getLatLng();
        form.latitude  = String(pos.lat.toFixed(7));
        form.longitude = String(pos.lng.toFixed(7));
        locationStatus.value = "تم تحديث الموقع";
      });
    }

    leafletMap.setView([lat, lng], leafletMap.getZoom() < 15 ? 16 : leafletMap.getZoom());
  }

  // ── Init inline map ───────────────────────────
  const DEFAULT_LAT = 24.7136;
  const DEFAULT_LNG = 46.6753;

  async function initMap(containerId = "inline-map-container") {
    try {
      const L = await loadLeaflet();
      await new Promise(r => setTimeout(r, 80));

      const container = document.getElementById(containerId);
      if (!container) return;

      if (leafletMap) {
        leafletMap.remove();
        leafletMap    = null;
        leafletMarker = null;
      }

      const startLat = form.latitude  ? Number(form.latitude)  : DEFAULT_LAT;
      const startLng = form.longitude ? Number(form.longitude) : DEFAULT_LNG;

      leafletMap = L.map(container, { zoomControl: true })
        .setView([startLat, startLng], form.latitude ? 16 : 11);

      // OSM standard tiles — keyless (CARTO free basemaps now require a key)
      L.tileLayer(
        "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
        {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          maxZoom:     19,
        }
      ).addTo(leafletMap);

      if (form.latitude && form.longitude) {
        placeMarker(L, Number(form.latitude), Number(form.longitude));
      }

      leafletMap.on("click", (e) => {
        placeMarker(L, e.latlng.lat, e.latlng.lng);
        locationStatus.value = "تم تحديد الموقع — اسحب الدبوس لضبطه";
      });

      setTimeout(() => leafletMap?.invalidateSize(), 200);
      mapReady.value = true;

    } catch (e) {
      console.error("Map init error:", e);
      locationStatus.value = "تعذر تحميل الخريطة";
    }
  }

  // ── GPS ───────────────────────────────────────
  async function getDeviceLocation() {
    error.value          = null;
    locationStatus.value = "";

    if (!navigator.geolocation) {
      locationStatus.value = "المتصفح لا يدعم تحديد الموقع";
      return;
    }

    locating.value       = true;
    locationStatus.value = "جاري تحديد الموقع...";

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;

        if (leafletMap) {
          placeMarker(window.L, lat, lng);
          locationStatus.value = "تم استخدام موقعك الحالي — تأكد أنك في المكان نفسه واسحب الدبوس للضبط";
        } else {
          form.latitude  = String(lat);
          form.longitude = String(lng);
          locationStatus.value = "تم استخدام موقعك الحالي — تأكد أنك في المكان نفسه";
          await initMap();
        }

        locating.value = false;
      },
      () => {
        locating.value       = false;
        locationStatus.value = "تعذر تحديد الموقع — اضغط على الخريطة لتحديده يدوياً";
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  }

  // ── Auto-center on mount ──────────────────────
  // Only centers the map near the user so tapping is easier. It must NEVER
  // fill the form coords — most users add a place later from home, and the
  // old auto-fill made their home the branch location.
  function tryAutoLocate() {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (leafletMap && !hasCoords.value) {
          leafletMap.setView([pos.coords.latitude, pos.coords.longitude], 13);
        }
      },
      () => {},
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  }

  // ── Google Maps URL → coords ──────────────────
  const resolvingLink = ref(false);
  const linkStatus    = ref("");   // "" | "ok" | "resolving" | "fail"
  const linkError     = ref("");   // user-facing message for the fail state

  function coordsInRange(lat, lng) {
    return Number.isFinite(lat) && Number.isFinite(lng) &&
           Math.abs(lat) <= 90 && Math.abs(lng) <= 180 &&
           !(lat === 0 && lng === 0);
  }

  // Extract lat/lng from a full Google Maps URL or a raw "lat, lng" paste.
  // Order matters: !3d!4d is the actual place pin; @lat,lng is only the
  // map center, so it comes last.
  function parseMapsUrl(text) {
    if (!text) return null;
    let s = text;
    try { s = decodeURIComponent(text); } catch { /* keep raw */ }
    const patterns = [
      /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/,
      /[?&]q=(?:loc:)?(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/,
      /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
      /^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/,
    ];
    for (const re of patterns) {
      const m = s.match(re);
      if (m) {
        const lat = Number(m[1]);
        const lng = Number(m[2]);
        if (coordsInRange(lat, lng)) return { lat, lng };
      }
    }
    return null;
  }

  function isGoogleMapsUrl(url) {
    return /(?:maps\.app\.goo\.gl|goo\.gl\/maps|google\.[a-z.]+\/maps|maps\.google\.)/i.test(url);
  }

  async function applyLinkCoords(lat, lng) {
    // Coords land in the form FIRST — even if the map below misbehaves,
    // the location is captured and submit keeps working.
    form.latitude  = String(lat);
    form.longitude = String(lng);
    linkStatus.value     = "ok";
    linkError.value      = "";
    locationStatus.value = "تم تحديد الموقع من الرابط — تأكد من موضع الدبوس على الخريطة";
    // The pin preview is cosmetic; never let a Leaflet error escape into
    // the page (it froze the whole form for some users).
    try {
      if (leafletMap && window.L) placeMarker(window.L, lat, lng);
      else await initMap();
    } catch { /* preview unavailable — coords already stored */ }
  }

  function failLink(message) {
    linkStatus.value = "fail";
    linkError.value  = message;
  }

  async function handleMapsUrl(url) {
    const local = parseMapsUrl(url);
    if (local) {
      await applyLinkCoords(local.lat, local.lng);
      return;
    }
    // Not a maps link at all — say so instead of silently doing nothing
    if (!isGoogleMapsUrl(url)) {
      failLink("الرابط غير مدعوم — الصق رابطاً من خرائط Google");
      return;
    }
    // Short/app links hide the coords behind a redirect the browser can't
    // follow (CORS) — ask the backend resolver. Typical response <1s; worst
    // case (slow Google + embed fallback) ~11s → 15s timeout.
    resolvingLink.value = true;
    linkStatus.value    = "resolving";
    try {
      const res = await api.post("/resolve-maps-url", { url }, { timeout: 15000 });
      const lat = Number(res.data?.lat);
      const lng = Number(res.data?.lng);
      if (coordsInRange(lat, lng)) await applyLinkCoords(lat, lng);
      else failLink("تعذّر استخراج الموقع من الرابط — حدّد الموقع يدوياً على الخريطة");
    } catch (e) {
      const status = e?.response?.status;
      if      (status === 404) failLink("تعذّر استخراج الموقع من الرابط — حدّد الموقع يدوياً على الخريطة");
      else if (status === 422) failLink("الرابط ليس رابط خرائط Google");
      else if (status === 429) failLink("محاولات كثيرة — انتظر دقيقة ثم أعد المحاولة");
      else                     failLink("تعذّر الاتصال — حاول مرة أخرى"); // timeout / network stall
    } finally {
      resolvingLink.value = false;
    }
  }

  // Explicit, user-triggered resolution — no watcher, no background work
  // while typing. One tap on «تحديد الموقع» (or Enter) runs it once.
  function clearLinkState() {
    linkStatus.value = "";
    linkError.value  = "";
  }

  async function resolveLink() {
    if (resolvingLink.value) return;
    const url = (form.location_URL || "").trim();
    if (!url) {
      failLink("الصق رابط المكان أولاً");
      return;
    }
    await handleMapsUrl(url);
  }

  // ── Submit validation ─────────────────────────
  const canSubmit = computed(() => {
    const baseOk      = hasName.value && !!form.district && !!form.country && cityIsValid.value;
    const hasLocation = hasCoords.value || !!form.location_URL;
    const hasImage    = !!form.main_image;
    return baseOk && hasLocation && hasImage;
  });

  // What's still blocking submit — shown under the disabled button so the
  // user knows exactly what to complete.
  const missingRequirements = computed(() => {
    const out = [];
    if (!hasName.value)                              out.push("اسم المكان");
    if (!form.country)                               out.push("الدولة");
    if (!cityIsValid.value)                          out.push("المدينة");
    if (!form.district)                              out.push("الحي");
    if (!(hasCoords.value || !!form.location_URL))   out.push("الموقع على الخريطة");
    if (!form.main_image)                            out.push("صورة المكان");
    return out;
  });

  // ── Image ─────────────────────────────────────
  function _applyFile(f) {
    if (!f) return;
    form.main_image = f;
    if (imagePreviewObjectUrl) URL.revokeObjectURL(imagePreviewObjectUrl);
    imageName.value           = f.name;
    imagePreviewObjectUrl     = URL.createObjectURL(f);
    imagePreview.value        = imagePreviewObjectUrl;
  }

  function onCameraChange(e)  { _applyFile(e.target.files?.[0]); e.target.value = ""; }
  function onGalleryChange(e) { _applyFile(e.target.files?.[0]); e.target.value = ""; }

  function clearImage() {
    form.main_image = null;
    imageName.value = "";
    if (imagePreviewObjectUrl) {
      URL.revokeObjectURL(imagePreviewObjectUrl);
      imagePreviewObjectUrl = null;
    }
    imagePreview.value = "";
  }

  // ── Submit ────────────────────────────────────
  async function submit() {
    if (loading.value) return;
    loading.value = true;
    error.value   = null;

    try {
      syncPreferredName();
      if (!canSubmit.value) throw new Error("تحقق من الحقول المطلوبة: اسم المكان + الموقع + صورة");

      const token = localStorage.getItem("token");
      if (!token) throw new Error("يجب تسجيل الدخول قبل إضافة مكان");

      const fd = new FormData();
      fd.append("company_name",   form.company_name);
      if (form.company_name_ar) fd.append("company_name_ar", form.company_name_ar);
      if (form.company_name_en) fd.append("company_name_en", form.company_name_en);
      fd.append("preferred_lang", form.preferred_lang);
      fd.append("district",       form.district);
      fd.append("city",           form.city);
      fd.append("country",        form.country);

      if (hasCoords.value) {
        fd.append("latitude",  form.latitude);
        fd.append("longitude", form.longitude);
      }
      // Always send the URL too when present — provenance for review,
      // and the only location if a short link couldn't be resolved.
      if (form.location_URL) {
        fd.append("location_URL", form.location_URL);
      }

      if (form.note) fd.append("note", form.note);
      fd.append("main_image", form.main_image);

      const res = await axios.post(`${apiUrl}/branches`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const branchId = res.data?.branch_id ?? res.data?.data?.id;

      if (branchId) {
        router.push({
          name:  "BranchDetails",
          params: { id: branchId },
          query:  { fromVote: "1" },
        });
      }

    } catch (e) {
      if (e?.response?.status === 401) localStorage.removeItem("token");
      if (e?.response?.data?.errors) {
        error.value = Object.values(e.response.data.errors).flat().join(" — ");
      } else {
        error.value = e?.response?.data?.message || e?.message || "حدث خطأ، حاول مجدداً";
      }
    } finally {
      loading.value = false;
    }
  }

  // ── Lifecycle ─────────────────────────────────
  onMounted(async () => {
    if (!form.country) form.country = "السعودية";
    syncPreferredName();
    await initMap();
    await tryAutoLocate();
  });

  onUnmounted(() => {
    if (leafletMap) { leafletMap.remove(); leafletMap = null; }
    if (imagePreviewObjectUrl) URL.revokeObjectURL(imagePreviewObjectUrl);
  });

  return {
    form,
    countries, saCities, citiesForCountry, cityQuery,
    cityDropdownOpen, filteredCities, cityIsValid,
    loading, locating, canSubmit, missingRequirements, error,
    locationStatus, hasCoords, hasName, coordsLabel,
    resolvingLink, linkStatus, linkError, resolveLink, clearLinkState,
    imageName, imagePreview, mapReady,
    syncPreferredName,
    pickCity, clearCity, onCityFocus, onCityBlur,
    onCameraChange, onGalleryChange, clearImage,
    getDeviceLocation, initMap,
    submit,
  };
}